import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { iotDataApi } from '../../services/iotDataApi';
import SimpleLineChart from './SimpleLineChart';
import MultiSeriesLineChart from './MultiSeriesLineChart';

const SUBVIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'waveform', label: 'Waveform' },
  { key: 'fft', label: 'FFT Spectrum' },
  { key: 'bearing', label: 'Bearing Analysis' },
  { key: 'trends', label: 'Vibration Trends' },
  { key: 'compare', label: 'Multi-Sensor Comparison' },
  { key: 'health', label: 'Asset Health' },
  { key: 'explorer', label: 'Data Explorer' },
];

const FEATURE_ORDER = [
  'VIB_RMS',
  'DE_VIB_RMS',
  'AXIAL_VIB_RMS',
  'VIB_1X_AMPLITUDE',
  'KURTOSIS',
  'VIB_KURTOSIS',
  'CREST_FACTOR',
];

const pickFeaturesFromCatalog = (catalogItems) => {
  const codes = (catalogItems || []).map((c) => c.feature_code).filter(Boolean);
  const preferred = FEATURE_ORDER.filter((c) => codes.includes(c));
  return preferred.length ? preferred : codes;
};

const sensorOption = (s) => {
  const id = String(s.astioDeviceId || s.astioExMeterCode || s.astioId || '');
  const label =
    s.astioDeviceName || s.astioDescription || s.astioExMeterCode || s.astioDeviceId || id;
  const type = s.astioExSensorType || s.sensorType || '';
  return { id, label, type };
};

const toPoints = (series) =>
  (series?.points || [])
    .filter((p) => p.value != null && (p.t || p.timestamp))
    .map((p) => ({ timestamp: p.t || p.timestamp, value: Number(p.value) }));

const sortFeatureSeries = (seriesList) => {
  const list = [...(seriesList || [])];
  list.sort((a, b) => {
    const ia = FEATURE_ORDER.indexOf(a.featureCode);
    const ib = FEATURE_ORDER.indexOf(b.featureCode);
    const ra = ia === -1 ? 99 : ia;
    const rb = ib === -1 ? 99 : ib;
    if (ra !== rb) return ra - rb;
    return String(a.featureCode || '').localeCompare(String(b.featureCode || ''));
  });
  return list;
};

/**
 * Vibration Analysis — current-schema slice (no new tables required).
 */
const VibrationAnalysisPanel = ({
  assetId,
  sensorId,
  sensors = [],
  telemetryAssetId,
  telemetrySensorId,
  dbCtdId,
  dateFrom,
  dateTo,
  assetLabel,
  sensorLabel,
}) => {
  const [subview, setSubview] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [waveforms, setWaveforms] = useState(null);
  const [spectra, setSpectra] = useState(null);
  const [bearing, setBearing] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [explorer, setExplorer] = useState(null);
  const [context, setContext] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [cursorT, setCursorT] = useState(null);
  const loadGenRef = useRef(0);
  const prevPrimaryRef = useRef('');

  const primarySensor = String(telemetrySensorId || sensorId || '');
  const resolvedAsset = telemetryAssetId || assetId;

  const featuresParam = useMemo(
    () => (selectedFeatures.length ? selectedFeatures.join(',') : undefined),
    [selectedFeatures]
  );

  const baseParams = useMemo(
    () => ({
      assetId: resolvedAsset,
      sensorId: primarySensor,
      dbCtdId,
      from: dateFrom,
      to: dateTo,
      ...(featuresParam ? { features: featuresParam } : {}),
    }),
    [resolvedAsset, primarySensor, dbCtdId, dateFrom, dateTo, featuresParam]
  );

  const compareParams = useMemo(() => {
    const ids = (compareIds.length ? compareIds : [primarySensor]).map(String).filter(Boolean);
    // Always include the primary Track sensor so Overview charts match KPIs.
    const withPrimary = primarySensor && !ids.includes(primarySensor)
      ? [primarySensor, ...ids]
      : ids;
    return {
      ...baseParams,
      sensors: withPrimary.join(','),
      sensorId: primarySensor || withPrimary[0],
    };
  }, [baseParams, compareIds, primarySensor]);

  // Sensor change: reset compare list + features so charts don't stick to the old meter.
  useEffect(() => {
    if (!primarySensor) return;
    if (prevPrimaryRef.current === primarySensor) return;
    prevPrimaryRef.current = primarySensor;
    setCompareIds([primarySensor]);
    setSelectedFeatures([]);
    setCursorT(null);
    setOverview(null);
    setTrends(null);
    setCatalog([]);
  }, [primarySensor]);

  const load = useCallback(async () => {
    if (!baseParams.assetId || !baseParams.sensorId || !baseParams.dbCtdId || !baseParams.from || !baseParams.to) {
      setError('Select asset, sensor, IoT DB, and From/To, then Apply.');
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError('');
    try {
      const primaryOnly = { ...baseParams, sensors: primarySensor };
      const [
        o,
        t,
        w,
        s,
        b,
        cat,
        exp,
        ctx,
      ] = await Promise.all([
        iotDataApi.getVibrationOverview(primaryOnly).then((r) => r.data),
        iotDataApi.getVibrationTrends(compareParams).then((r) => r.data),
        iotDataApi.getVibrationWaveforms(primaryOnly).then((r) => r.data),
        iotDataApi.getVibrationSpectra({ ...primaryOnly, spectrumType: 'fft' }).then((r) => r.data),
        iotDataApi.getVibrationBearing(primaryOnly).then((r) => r.data),
        iotDataApi.getVibrationCatalog(primaryOnly).then((r) => r.data),
        iotDataApi.getVibrationExplorer(compareParams).then((r) => r.data),
        iotDataApi
          .getVibrationContext({
            assetId: baseParams.assetId,
            dbCtdId: baseParams.dbCtdId,
            from: baseParams.from,
            to: baseParams.to,
          })
          .then((r) => r.data),
      ]);
      if (gen !== loadGenRef.current) return;
      const items = cat?.items || [];
      setOverview(o);
      setTrends(t);
      setWaveforms(w);
      setSpectra(s);
      setBearing(b);
      setCatalog(items);
      setExplorer(exp);
      setContext(ctx);
      setSelectedFeatures((prev) => {
        const next = pickFeaturesFromCatalog(items);
        if (!prev.length) return next;
        const allowed = new Set(next);
        const kept = prev.filter((c) => allowed.has(c));
        return kept.length ? kept : next;
      });
      if (o?.quality?.status === 'error' && o?.quality?.message) setError(o.quality.message);
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setError(e.response?.data?.detail || e.message || 'Vibration analysis failed');
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [baseParams, compareParams, primarySensor]);

  useEffect(() => {
    load();
  }, [load]);

  const compareSensors = useMemo(
    () => (sensors || []).map(sensorOption).filter((s) => s.id),
    [sensors]
  );

  const featureSeries = useMemo(() => {
    const sorted = sortFeatureSeries(trends?.series || []);
    if (!selectedFeatures.length) return sorted;
    const allow = new Set(selectedFeatures);
    return sorted.filter((s) => allow.has(s.featureCode));
  }, [trends, selectedFeatures]);

  /** One panel per feature (RMS / kurtosis / crest …) for primary+compare sensors. */
  const panelsByFeature = useMemo(() => {
    const map = new Map();
    featureSeries.forEach((s) => {
      const code = s.featureCode;
      if (!map.has(code)) {
        map.set(code, {
          featureCode: code,
          featureName: s.featureName || code,
          unit: s.unit || '',
          series: [],
        });
      }
      const label =
        compareSensors.find((x) => String(x.id) === String(s.sensorId))?.label ||
        s.sensorId ||
        code;
      map.get(code).series.push({
        key: `${s.sensorId || 's'}-${code}`,
        label: String(label),
        points: toPoints(s),
      });
    });
    return Array.from(map.values());
  }, [featureSeries, compareSensors]);

  const compareOverlay = useMemo(() => {
    const prefer =
      panelsByFeature.find((p) => p.featureCode === 'VIB_RMS') || panelsByFeature[0];
    return prefer || null;
  }, [panelsByFeature]);

  const contextSeries = useMemo(
    () =>
      (context?.series || []).map((s) => ({
        key: s.sensorId,
        label: s.label || s.sensorId,
        points: toPoints(s),
      })),
    [context]
  );

  const explorerRows = useMemo(
    () =>
      (explorer?.items || []).map((r, i) => ({
        id: `${r.sensor_id}-${r.feature_code}-${r.measured_at}-${i}`,
        ...r,
      })),
    [explorer]
  );

  const empty = (text) => (
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  );

  const featurePicker = (
    <FormControl size="small" sx={{ minWidth: 280, maxWidth: 520 }}>
      <InputLabel id="vib-feat-label">Features (FR-09)</InputLabel>
      <Select
        labelId="vib-feat-label"
        multiple
        value={selectedFeatures}
        onChange={(e) => setSelectedFeatures(e.target.value)}
        input={<OutlinedInput label="Features (FR-09)" />}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((code) => (
              <Chip key={code} size="small" label={code} />
            ))}
          </Box>
        )}
      >
        {catalog.map((c) => (
          <MenuItem key={c.feature_code} value={c.feature_code} sx={{ fontSize: '0.8rem' }}>
            {c.feature_name || c.feature_code}
            {c.unit ? ` (${c.unit})` : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const cursorNote = cursorT ? (
    <Typography variant="caption" color="text.secondary">
      Shared cursor: {cursorT}
    </Typography>
  ) : null;

  const contextStrip = (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
      <Typography variant="caption" fontWeight={700} sx={{ px: 0.5 }}>
        Operating context (RPM / temp / current / load)
      </Typography>
      {contextSeries.length ? (
        <MultiSeriesLineChart
          series={contextSeries}
          height={160}
          unit={context?.series?.[0]?.unit || ''}
          selectedTimestamp={cursorT}
          onPointClick={(p) => setCursorT(p?.timestamp || null)}
        />
      ) : (
        empty(context?.message || 'No operating-context series for this asset/window.')
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f7f9fb',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Vibration Analysis
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {assetLabel || baseParams.assetId} · {sensorLabel || baseParams.sensorId}
            {overview?.window?.from ? ` · ${overview.window.from} → ${overview.window.to}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {featurePicker}
          {cursorNote}
          {loading ? <CircularProgress size={16} /> : null}
        </Stack>
      </Box>

      <Tabs
        value={subview}
        onChange={(_, v) => setSubview(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 32,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 32,
            py: 0.25,
            px: 1.25,
            textTransform: 'none',
            fontSize: '0.72rem',
          },
        }}
      >
        {SUBVIEWS.map((s) => (
          <Tab key={s.key} value={s.key} label={s.label} />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.25 }}>
        {error ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        {subview === 'overview' && (
          <Stack spacing={1.25}>
            {overview?.quality?.message ? (
              <Alert severity={overview.quality.status === 'ok' ? 'info' : 'warning'}>
                {overview.quality.message}
              </Alert>
            ) : null}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {(overview?.kpis || []).map((k) => (
                <Box
                  key={`${k.sensor_id || ''}-${k.feature_code}`}
                  sx={{
                    minWidth: 140,
                    px: 1.25,
                    py: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#fafcfd',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {k.feature_name || k.feature_code}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {k.value != null ? Number(k.value).toFixed(3) : '—'}
                    <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                      {k.unit || ''}
                    </Typography>
                  </Typography>
                </Box>
              ))}
              {!overview?.kpis?.length && !loading
                ? empty('No KPI values for the selected sensors and window.')
                : null}
            </Stack>

            {contextStrip}

            {panelsByFeature.map((panel) => {
              const primary =
                panel.series.find((s) => String(s.key).startsWith(`${primarySensor}-`)) ||
                panel.series[0];
              const pts = primary?.points || [];
              const base = trends?.baselines?.find((b) => b.feature_code === panel.featureCode);
              if (!pts.length) return null;
              return (
                <Box
                  key={`${primarySensor}-${panel.featureCode}`}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ px: 0.5 }}>
                    {panel.featureName || panel.featureCode}
                  </Typography>
                  <SimpleLineChart
                    points={pts}
                    baseline={base?.baseline_value != null ? Number(base.baseline_value) : undefined}
                    unit={panel.unit || ''}
                    height={200}
                    compact
                    selectedTimestamp={cursorT}
                    onPointClick={(p) => setCursorT(p?.timestamp || null)}
                  />
                </Box>
              );
            })}
            {!loading && overview?.kpis?.length && !panelsByFeature.some((p) => p.series.some((s) => s.points?.length))
              ? empty('KPI loaded but no trend points for this sensor/window.')
              : null}
          </Stack>
        )}

        {subview === 'waveform' &&
          empty(waveforms?.reason || 'Waveform metadata deferred (new table).')}

        {subview === 'fft' &&
          empty(spectra?.reason || 'FFT spectrum deferred (vibration_spectrum).')}

        {subview === 'bearing' &&
          empty(bearing?.reason || 'Bearing analysis deferred (Phase 2).')}

        {subview === 'trends' && (
          <Stack spacing={1.25}>
            {contextStrip}
            {panelsByFeature.map((panel) => (
              <Box
                key={panel.featureCode}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75 }}
              >
                <Typography variant="caption" fontWeight={700}>
                  {panel.featureName || panel.featureCode}
                  {panel.unit ? ` (${panel.unit})` : ''}
                </Typography>
                {panel.series.length > 1 ? (
                  <MultiSeriesLineChart
                    series={panel.series}
                    height={180}
                    unit={panel.unit}
                    selectedTimestamp={cursorT}
                    onPointClick={(p) => setCursorT(p?.timestamp || null)}
                  />
                ) : (
                  <SimpleLineChart
                    points={panel.series[0]?.points || []}
                    baseline={
                      trends?.baselines?.find((b) => b.feature_code === panel.featureCode)
                        ?.baseline_value != null
                        ? Number(
                            trends.baselines.find((b) => b.feature_code === panel.featureCode)
                              .baseline_value
                          )
                        : undefined
                    }
                    unit={panel.unit || ''}
                    height={180}
                    compact
                    selectedTimestamp={cursorT}
                    onPointClick={(p) => setCursorT(p?.timestamp || null)}
                  />
                )}
              </Box>
            ))}
            {!panelsByFeature.length && !loading
              ? empty('No vibration trends in this window.')
              : null}
          </Stack>
        )}

        {subview === 'compare' && (
          <Stack spacing={1.25}>
            <FormControl size="small" sx={{ maxWidth: 560 }}>
              <InputLabel id="vib-compare-label">Sensors</InputLabel>
              <Select
                labelId="vib-compare-label"
                multiple
                value={compareIds}
                onChange={(e) => setCompareIds(e.target.value)}
                input={<OutlinedInput label="Sensors" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const s = compareSensors.find((x) => String(x.id) === String(id));
                      return (
                        <Chip
                          key={id}
                          size="small"
                          label={s?.type ? `${s.type}: ${s.label}` : s?.label || id}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {sensors.map((row) => {
                  const s = sensorOption(row);
                  return (
                    <MenuItem
                      key={row.astioId || s.id}
                      value={String(s.id || row.astioId)}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {s.type ? `[${s.type}] ${s.label}` : s.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {compareOverlay?.series?.length ? (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
                <Typography variant="caption" fontWeight={700}>
                  Overlay · {compareOverlay.featureName || compareOverlay.featureCode}
                </Typography>
                <MultiSeriesLineChart
                  series={compareOverlay.series}
                  height={240}
                  unit={compareOverlay.unit}
                  selectedTimestamp={cursorT}
                  onPointClick={(p) => setCursorT(p?.timestamp || null)}
                />
              </Box>
            ) : (
              empty('Select two or more sensors (or one with feature data) to compare.')
            )}
            {contextStrip}
          </Stack>
        )}

        {subview === 'health' &&
          (overview?.health ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: '#fafcfd',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.72rem',
                overflow: 'auto',
              }}
            >
              {JSON.stringify(overview.health, null, 2)}
            </Box>
          ) : (
            empty('No asset health score for this asset (table optional / Phase 3).')
          ))}

        {subview === 'explorer' && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              sensor_feature rows for selected sensors / FR-09 features / window
              {explorer?.total != null ? ` · showing ${explorer.total}` : ''}
            </Typography>
            <DataGrid
              rows={explorerRows}
              columns={[
                { field: 'sensor_id', headerName: 'Sensor', flex: 0.7, minWidth: 90 },
                { field: 'feature_code', headerName: 'Feature', flex: 0.9, minWidth: 110 },
                { field: 'feature_name', headerName: 'Name', flex: 1.1, minWidth: 140 },
                { field: 'value', headerName: 'Value', flex: 0.6, minWidth: 90, type: 'number' },
                { field: 'unit', headerName: 'Unit', flex: 0.45, minWidth: 60 },
                { field: 'measured_at', headerName: 'Measured At', flex: 1, minWidth: 150 },
                { field: 'operating_state', headerName: 'State', flex: 0.7, minWidth: 100 },
                { field: 'z_score', headerName: 'z', flex: 0.5, minWidth: 70, type: 'number' },
              ]}
              density="compact"
              autoHeight
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                if (params?.row?.measured_at) setCursorT(params.row.measured_at);
              }}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            />
            {!explorerRows.length && !loading
              ? empty(explorer?.message || 'No explorer rows for this selection.')
              : null}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default VibrationAnalysisPanel;
